import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AppRouting } from '../../app-routing.module';
import { PickupService } from '../../services/pickup.service';
import { ApiPublicService } from '../../../lib/lib-ngx/web-services/api-public.service';
import { PRICEMULTIPLIER } from '../../../lib/lib-shared/defines';
import { IKit } from '../../../lib/lib-shared/models/Kit';
import { IOrderDetailsListOfKitBatch } from '../../../lib/lib-shared/models/Order';
import { OrderStatusStatus } from '../../../lib/lib-shared/types/OrderStatusStatus';
import { ConfirmationModalComponent } from '../../modals/confirmation-modal/confirmation-modal.component';
import { TPickupPostIdRep, TPickupPutIdRep } from '../../../lib/lib-shared/api/Pickup.Type';
import { PickupOwnerAdvertising } from '../../../lib/lib-shared/types/PickupOwnerAdvertising';
import { PickupOwnerModalComponent } from '../../modals/pickup-owner-modal/pickup-owner-modal.component';
import { IPickupOwnerModalOutput } from '../../modals/pickup-owner-modal/pickup-owner-modal.component.types';

@Component({
  selector: 'app-order-list',
  standalone: false,
  templateUrl: './order-list.component.html',
  styleUrl: './order-list.component.scss'
})
export class OrderListComponent implements OnInit {
  public PRICEMULTIPLIER = PRICEMULTIPLIER;

  private kitList: IKit[] = [];
  public orderList: IOrderDetailsListOfKitBatch[] = [];
  public trackingRefsMap: Record<number, string[]> = {};

  constructor(
    private router: Router,
    public pickupService: PickupService,
    private apiPublicService: ApiPublicService,
    private modalService: NgbModal,
  ) {
  }

  ngOnInit() {
    if (!this.pickupService.getPickup()!.pickupOwner) {
      this.onShowPickupOwnerModal();
    } else {
      this.loadData();
    }
  }

  async onShowPickupOwnerModal() {
    const pickupOwnerModalComponent = this.modalService.open(PickupOwnerModalComponent, {
      size: 'xl' as 'lg',
      backdrop: 'static',
      keyboard: false,
      centered: true
    });
    pickupOwnerModalComponent.componentInstance.pickupOwner = this.pickupService.getPickup()!.pickupOwner;
    pickupOwnerModalComponent.componentInstance.isPickupMode = this.pickupService.getPickupMode();
    const pickupOwnerData: IPickupOwnerModalOutput = await pickupOwnerModalComponent.result;

    let obs: Observable<TPickupPutIdRep & TPickupPostIdRep>;
    if (this.pickupService.getPickup()!.pickupOwner) {
      obs = this.apiPublicService.putPickupId(this.pickupService.getPickup()!.id, pickupOwnerData);
    } else {
      obs = this.apiPublicService.postPickup({pickupId: this.pickupService.getPickup()!.id, ...pickupOwnerData});
    }

    obs
      .subscribe(() => {
        this.apiPublicService.getPickupId(this.pickupService.getPickup()!.id, {})
          .subscribe(data => {
            if (data) {
              this.pickupService.setPickup(data);
              this.loadData();
            }
          });
      });
  }

  loadData() {
    forkJoin([
      this.apiPublicService.getKit({}),
      this.apiPublicService.getOrderKitBatch({pickupId: this.pickupService.getPickup()!.id}),
    ])
      .subscribe(([kitList, orderList]) => {
        this.kitList = kitList;
        this.orderList = orderList;
        this.trackingRefsMap = Object.fromEntries(orderList.map(order => [
          order.id,
          order.orderShipReturnList.flatMap(osr => osr.transportSheetList.map(ts => ts.ref)).filter(Boolean),
        ]));
      });
  }

  hasAlreadyReceivedAnOrder(): boolean {
    return this.orderList.length > 0;
  }

  async onCreateKitBatch(defaultQty: number, othersQty: number) {
    const confirmationModal = this.modalService.open(ConfirmationModalComponent, {
      size: 'xl' as 'lg',
      backdrop: 'static',
      keyboard: false,
      centered: true
    });

    if (this.orderList.find(o => o.status === OrderStatusStatus.CREATE)) {
      confirmationModal.componentInstance.confirmationDesc = 'Vous ne pouvez pas avoir plusieurs commandes en cours en même temps.';
      confirmationModal.componentInstance.validateButtonText = 'Fermer';
    } else {
      confirmationModal.componentInstance.confirmationDesc = 'Confirmer une demande de création de commande ?';
      confirmationModal.componentInstance.validateButtonText = 'Confirmer';
      confirmationModal.componentInstance.cancelButtonText = 'Annuler';
      const result = await confirmationModal.result;

      if (result) {
        const defaultKit = this.kitList.find(k => k.default);
        const otherKitList = this.kitList.filter(k => !k.default);

        const kitList: { kitId: number, quantity: number }[] = [{
          kitId: defaultKit!.id,
          quantity: defaultQty * PRICEMULTIPLIER
        }];
        for (const kit of otherKitList) {
          kitList.push({kitId: kit.id, quantity: othersQty * PRICEMULTIPLIER});
        }

        this.apiPublicService.postOrderKitBatch({
          pickupId: this.pickupService.getPickup()!.id,
          kitList: kitList,
        })
          .subscribe(() => this.loadData());
      }
    }
  }

  onLogout() {
    this.pickupService.setPickup(null);
    this.router.navigate([AppRouting.LOGIN]);
  }
}
